import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { Select } from 'antd'
import { useCallback, useRef, useState } from 'react'

import { VODOPAD_ENDPOINT } from '@/constants/constants'
import type { PaginatedItems } from '@/types/api'

type AdminUserDto = components['schemas']['AdminUserDto']

interface Props {
  placeholder?: string
  style?: React.CSSProperties
  valueField?: 'id' | 'email'
  value?: string
  defaultValue?: string
  onChange: (value: string | undefined) => void
}

export function UserSearchSelect({
  placeholder = 'Search user by email...',
  style,
  valueField = 'id',
  value,
  defaultValue,
  onChange,
}: Props) {
  const [search, setSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const usersQuery = useCustom<PaginatedItems<AdminUserDto>>({
    url: `${VODOPAD_ENDPOINT}/v2/admin/users`,
    method: 'get',
    config: { query: { email: search, page: 1, perPage: 10 } },
    queryOptions: { enabled: search.length >= 2 },
  })

  const users =
    (usersQuery.query.data?.data as PaginatedItems<AdminUserDto> | undefined)
      ?.items ?? []

  const handleSearch = useCallback((value: string) => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearch(value)
    }, 300)
  }, [])

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      style={style ?? { width: 250 }}
      value={value}
      defaultValue={defaultValue}
      filterOption={false}
      onSearch={handleSearch}
      onChange={(value: string | undefined) => onChange(value)}
      loading={usersQuery.query.isLoading}
      options={users.map((u) => ({
        label: u.email,
        value: valueField === 'email' ? u.email : u.id,
      }))}
      notFoundContent={
        search.length < 2 ? 'Type at least 2 characters' : 'No users found'
      }
    />
  )
}
